import React, { useContext, useState } from "react";
import "./Agreements.css";
import AddAgreement from "./AddAgreement/AddAgreement";
import SendAgreementApprovalModal from "./SendAgreementApprovalModal/SendAgreementApprovalModal";
import AgreementsRegistry from "../../AgreementsRegistry/AgreementsRegistry";
import {
  canEditAgreement,
  canSendAgreementForApproval,
  canShowDeleteApprovalAction,
  getEditAgreementTitle,
  getDeleteApprovalTitle,
  getSendForApprovalTitle,
  isAgreementApprovalInitiator,
  resolveAgreementApprovalId,
} from "./agreementsListHelpers";
import {
  deleteAgreement,
  deleteAgreementApproval,
} from "../../../../api/agreementsApi";
import { UserContext } from "../../../../contexts/CurrentUserContext";

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" fill="none" aria-hidden>
    <path
      d="M11.3333 2.00001C11.5084 1.82491 11.7163 1.68605 11.9447 1.59128C12.1731 1.49652 12.4173 1.44775 12.6667 1.44775C12.916 1.44775 13.1602 1.49652 13.3886 1.59128C13.617 1.68605 13.8249 1.82491 14 2.00001C14.1751 2.1751 14.314 2.38305 14.4087 2.61143C14.5035 2.83981 14.5523 3.08405 14.5523 3.33334C14.5523 3.58263 14.5035 3.82687 14.4087 4.05525C14.314 4.28363 14.1751 4.49158 14 4.66668L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00001Z"
      stroke="currentColor"
      strokeWidth="1.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Agreements = ({ projectId }) => {
  const { userData } = useContext(UserContext);
  const [reloadToken, setReloadToken] = useState(0);
  const [error, setError] = useState(null);
  const [agreementModal, setAgreementModal] = useState({
    open: false,
    agreementId: null,
  });
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteApprovalAgreement, setPendingDeleteApprovalAgreement] =
    useState(null);
  const [isDeletingApproval, setIsDeletingApproval] = useState(false);
  const [agreementToApprove, setAgreementToApprove] = useState(null);

  const refreshAgreements = () => setReloadToken((token) => token + 1);

  const handleSendForApprovalClick = (agreement) => {
    setAgreementToApprove(agreement);
  };

  const cancelDelete = () => {
    if (!isDeleting) setPendingDeleteId(null);
  };

  const confirmDelete = async () => {
    if (pendingDeleteId == null) return;
    setIsDeleting(true);
    try {
      await deleteAgreement(pendingDeleteId);
      setPendingDeleteId(null);
      refreshAgreements();
    } catch (err) {
      setError(err.message || "Не удалось удалить соглашение");
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDeleteApproval = () => {
    if (!isDeletingApproval) setPendingDeleteApprovalAgreement(null);
  };

  const confirmDeleteApproval = async () => {
    if (!pendingDeleteApprovalAgreement) return;
    if (
      !isAgreementApprovalInitiator(
        null,
        pendingDeleteApprovalAgreement,
        userData?.id,
      )
    ) {
      setError("Отменить согласование может только инициатор");
      setPendingDeleteApprovalAgreement(null);
      return;
    }
    setIsDeletingApproval(true);
    try {
      const approvalId = await resolveAgreementApprovalId(
        pendingDeleteApprovalAgreement,
      );
      if (approvalId == null) {
        throw new Error("Процесс согласования не найден");
      }
      await deleteAgreementApproval(approvalId);
      setPendingDeleteApprovalAgreement(null);
      refreshAgreements();
    } catch (err) {
      setError(err.message || "Не удалось отменить согласование");
    } finally {
      setIsDeletingApproval(false);
    }
  };

  const renderActions = (agreement) => (
    <div className="agreement-actions">
      <button
        type="button"
        className="action-btn edit-btn"
        title={getEditAgreementTitle(agreement)}
        disabled={!canEditAgreement(agreement)}
        aria-label={getEditAgreementTitle(agreement)}
        onClick={() => {
          if (!canEditAgreement(agreement)) return;
          setAgreementModal({
            open: true,
            agreementId: agreement.id,
          });
        }}
      >
        <EditIcon />
      </button>
      {canSendAgreementForApproval(agreement) && (
        <button
          type="button"
          className="action-btn approve-btn"
          title={getSendForApprovalTitle(agreement)}
          onClick={() => handleSendForApprovalClick(agreement)}
        >
          📤
        </button>
      )}
      {canShowDeleteApprovalAction(agreement, userData?.id) && (
        <button
          type="button"
          className="action-btn cancel-approval-btn"
          title={getDeleteApprovalTitle(agreement)}
          onClick={() => setPendingDeleteApprovalAgreement(agreement)}
        >
          ↩
        </button>
      )}
      <button
        type="button"
        className="action-btn delete-btn"
        title="Удалить"
        onClick={() => setPendingDeleteId(agreement.id)}
      >
        🗑
      </button>
    </div>
  );

  return (
    <div className="agreements-container">
      <div className="agreements-header">
        <h2 className="agreements-title">Субподрядчики проекта</h2>
        <button
          className="agreements-add-btn"
          onClick={() => setAgreementModal({ open: true, agreementId: null })}
        >
          <span>+</span> Добавить соглашение
        </button>
      </div>

      {error && (
        <div className="agreements-error" role="alert">
          {error}
        </div>
      )}

      <AgreementsRegistry
        projectId={projectId}
        reloadToken={reloadToken}
        renderActions={renderActions}
      />

      <AddAgreement
        open={agreementModal.open}
        agreementId={agreementModal.agreementId}
        onClose={() => {
          refreshAgreements();
          setAgreementModal({ open: false, agreementId: null });
        }}
        projectId={projectId}
      />

      <SendAgreementApprovalModal
        open={agreementToApprove != null}
        agreement={agreementToApprove}
        onClose={() => setAgreementToApprove(null)}
        onSuccess={refreshAgreements}
      />

      {pendingDeleteApprovalAgreement != null && (
        <div
          className="agreements-popup-overlay"
          onClick={cancelDeleteApproval}
        >
          <div
            className="agreements-popup agreements-popup--confirm"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agreements-delete-approval-confirm-title"
          >
            <p
              id="agreements-delete-approval-confirm-title"
              className="agreements-popup-text"
            >
              {getDeleteApprovalTitle(pendingDeleteApprovalAgreement)}
            </p>
            <div className="agreements-popup-buttons">
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--cancel"
                onClick={cancelDeleteApproval}
                disabled={isDeletingApproval}
              >
                Нет
              </button>
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--confirm"
                onClick={confirmDeleteApproval}
                disabled={isDeletingApproval}
              >
                {isDeletingApproval ? "Удаление..." : "Отменить согласование"}
              </button>
            </div>
          </div>
        </div>
      )}

      {pendingDeleteId != null && (
        <div className="agreements-popup-overlay" onClick={cancelDelete}>
          <div
            className="agreements-popup agreements-popup--confirm"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="agreements-delete-confirm-title"
          >
            <p
              id="agreements-delete-confirm-title"
              className="agreements-popup-text"
            >
              Вы уверены что хотите удалить соглашение?
            </p>
            <div className="agreements-popup-buttons">
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--cancel"
                onClick={cancelDelete}
                disabled={isDeleting}
              >
                Отмена
              </button>
              <button
                type="button"
                className="agreements-popup-btn agreements-popup-btn--confirm"
                onClick={confirmDelete}
                disabled={isDeleting}
              >
                {isDeleting ? "Удаление..." : "Удалить"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Agreements;
